/**
 * English: Common words
 *
 * What this file covers: Buttons, statuses, table controls, short notices, countries, phone numbers, password
 * rules and contact attributes used on many pages.
 *
 * Tone: The shortest everyday words. Buttons are verbs (Save, Cancel, Remove). Use one word per idea and never
 * vary it, because these words sit beside every other file's text.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const common: Catalog = {
  'action.signOut': 'Sign out',
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.create': 'Create',
  'action.delete': 'Delete',
  'action.search': 'Search',
  'common.loading': 'Loading…',
  'common.language': 'Language',
  'common.plusMore': '+{{count}} more',
  'common.added': 'Added',
  'common.copyFailed': 'Could not copy',
  'common.unknownError': 'Unknown error',
  'common.name': 'Name',
  'common.email': 'Email',
  'common.status': 'Status',
  'common.type': 'Type',
  'common.description': 'Description',
  'common.created': 'Created',
  'common.updated': 'Updated',
  'common.actions': 'Actions',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.all': 'All',
  'common.none': 'None',
  'common.search': 'Search',
  'common.searchPlaceholder': 'Search…',
  'common.noResults': 'No results.',
  'common.date': 'Date',
  'common.amount': 'Amount',
  'common.total': 'Total',
  'common.details': 'Details',
  'common.overview': 'Overview',
  'common.optional': 'Optional',
  'common.required': 'Required',
  'common.copied': 'Copied to clipboard.',
  'common.enabled': 'Enabled',
  'common.disabled': 'Disabled',
  'common.never': 'Never',
  'common.unknown': 'Unknown',
  'common.select': 'Select…',
  'common.you': 'You',
  'common.notAvailable': 'n/a',
  'action.edit': 'Edit',
  'action.close': 'Close',
  'action.refresh': 'Refresh',
  'action.retry': 'Retry',
  'action.back': 'Back',
  'action.apply': 'Apply',
  'action.clear': 'Clear',
  'action.copy': 'Copy',
  'common.download': 'Download',
  'action.view': 'View',
  'action.confirm': 'Confirm',
  'action.add': 'Add',
  'action.remove': 'Remove',
  'action.submit': 'Submit',
  'action.send': 'Send',
  'action.continue': 'Continue',
  'action.next': 'Next',
  'action.previous': 'Previous',
  'action.download': 'Download',
  'action.saveChanges': 'Save changes',
  'action.discard': 'Discard',
  'action.select': 'Select',
  'action.open': 'Open',
  'action.enable': 'Enable',
  'action.disable': 'Disable',
  'action.activate': 'Activate',
  'action.pause': 'Pause',
  'action.start': 'Start',
  'action.signIn': 'Sign in',
  'action.signUp': 'Sign up',
  'action.export': 'Export',
  'action.import': 'Import',
  'action.revoke': 'Revoke',
  'action.regenerate': 'Regenerate',
  'action.keep': 'Keep',
  'status.active': 'Active',
  'status.inactive': 'Inactive',
  'status.pending': 'Pending',
  'status.approved': 'Approved',
  'status.rejected': 'Rejected',
  'status.failed': 'Failed',
  'status.sent': 'Sent',
  'status.delivered': 'Delivered',
  'status.read': 'Read',
  'status.draft': 'Draft',
  'status.submitted': 'Submitted',
  'status.paused': 'Paused',
  'status.running': 'Running',
  'status.completed': 'Completed',
  'status.cancelled': 'Cancelled',
  'status.queued': 'Queued',
  'status.scheduled': 'Scheduled',
  'status.expired': 'Expired',
  'status.revoked': 'Revoked',
  'status.verified': 'Verified',
  'status.unverified': 'Unverified',
  'status.open': 'Open',
  'status.closed': 'Closed',
  'status.resolved': 'Resolved',
  'status.archived': 'Archived',
  'status.unknown': 'Unknown',
  'toast.saved': 'Saved',
  'toast.deleted': 'Deleted',
  'toast.copied': 'Copied',
  'toast.validation': 'Validation',
  'toast.saveFailed': 'Save failed',
  'toast.deleteFailed': 'Delete failed',
  'toast.loadFailed': 'Failed to load',
  'notFound.title': 'Page not found',
  'notFound.attempted': 'You asked for',
  'notFound.didYouMean': 'Did you mean',
  'notFound.description': 'The page you\'re looking for doesn\'t exist or has moved.',
  'notFound.backToDashboard': 'Back to dashboard',
  'country.za': 'South Africa',
  'country.ng': 'Nigeria',
  'country.ke': 'Kenya',
  'action.undo': 'Undo',
  'password.rules.summary': 'At least 12 characters. Common leaked passwords are not accepted.',
  'password.rules.length': 'At least 12 characters',
  'password.rules.upper': 'An uppercase letter',
  'password.rules.lower': 'A lowercase letter',
  'password.rules.digit': 'A number',
  'password.rules.symbol': 'A symbol',
  'password.strength.label': 'Password strength',
  'action.dismiss': 'Dismiss',
  'table.view.table': 'Table',
  'table.view.cards': 'Grid',
  'table.view.list': 'List',
  'table.search': 'Search',
  'table.perPage': 'Rows per page',
  'table.perPageOption': '{{n}} / page',
  'table.noRecords': 'No records',
  'table.range': '{{from}}–{{to}} of {{total}}',
  'action.loadMore': 'Load more',
  'phone.savedAs': 'Will be saved as {{number}}',
  'phone.search': 'Search by country or code',
  'phone.noMatches': 'No country matches',
  'phone.country': 'Country code',
  'phone.reason.empty': 'Enter a phone number.',
  'phone.reason.notANumber': 'A phone number has only digits, and may start with +.',
  'phone.reason.tooShort': 'Too short for {{country}} (+{{dial}}): a number there has {{expected}} digits after the country code.',
  'phone.reason.tooLong': 'Too long for {{country}} (+{{dial}}): a number there has {{expected}} digits after the country code.',
  'phone.reason.unknownCountry': 'That country code is not one we know. Check the digits after the +.',
  'phone.reason.noCountry': 'Start with + and the country code, for example +27.',
  'phone.fixNumber': 'Check the phone number before continuing.',
  'phone.hint': 'Type it as you would dial it. We add the country code.',
  'common.or': 'or',
  'attributes.group.contact': 'Contact',
  'attributes.group.fields': 'Your fields',
  'attributes.group.flow': 'Flow variables',
  'attributes.contact.name': 'Name',
  'attributes.contact.email': 'Email',
  'attributes.contact.msisdn': 'Phone number',
  'attributes.contact.locale': 'Language',
  'attributes.fallbackHint': 'Add | and a fallback after the name, for anyone who has nothing there: contact.name|there',
  'attributes.flowFallbackHint': 'Add ?? and a fallback after the name, for when it is empty: contact.name ?? there',
  'password.errors.breached': 'That password is one of the most common leaked passwords. Choose one that is your own.',
  'password.errors.tooShort': 'A password needs at least 12 characters.',
  'action.change': 'Change',
  'common.words.add': 'Type and press Enter',
  'common.words.remove': 'Remove {{word}}',
  'common.words.full': 'That is the most: {{max}}'
};

export default common;
